import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { Div, Text } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { Palette } from 'src/components';

const TOTAL_COLORS = 22;

function VerticalBar({
	title,
	labels,
	series,
	style,
	options,
	formatter,
	onMouseover,
	theme: themeProp,
	onThemeChange,
	gradient: gradientProp,
	onGradientChange,
	markLine,
	usePalette,
	showPalette,
	spreads: spreadsProp,
	defaultTheme,
}) {
	const [gradient, setGradient] = useState(gradientProp);
	const [theme, setTheme] = useState(themeProp);
	const [spreads, setSpreads] = useState(spreadsProp);

	useEffect(() => {
		setGradient(gradientProp);
	}, [gradientProp]);

	useEffect(() => {
		setTheme(themeProp);
	}, [themeProp]);

	useEffect(() => {
		setSpreads(spreadsProp);
	}, [spreadsProp]);

	const allValues = series.reduce((accum, { values }) => [...accum, ...values], []);
	const maxLift = Math.max(...allValues);
	const minLift = Math.min(...allValues);
	const belowOneRange = 1 - minLift;
	const totalRange = maxLift - minLift;
	const belowOneTotalColors = (belowOneRange * TOTAL_COLORS) / totalRange - 1;
	const aboveOneTotalColors = TOTAL_COLORS - belowOneTotalColors;

	const seriesList = [];
	for (let i = 0; i < series.length; i += 1) {
		seriesList.push({
			name: series[i].name,
			type: 'bar',
			data: series[i].values.map((value) =>
				value < 0
					? {
							value,
							label: {
								...style.VerticalBar.label,
								formatter,
								position: series[i].labelPosition ? series[i].labelPosition : 'insideBottom',
							},
							itemStyle: {
								barBorderRadius: series[i].stack ? (i % 2 === 0 ? [0, 0, 0, 0] : [0, 0, 4, 4]) : [0, 0, 4, 4],
							},
						}
					: {
							value,
							label: {
								...style.VerticalBar.label,
								formatter,
								position: series[i].labelPosition ? series[i].labelPosition : 'insideTop',
							},
							itemStyle: {
								barBorderRadius: series[i].stack ? (i % 2 === 0 ? [0, 0, 0, 0] : [4, 4, 0, 0]) : [4, 4, 0, 0],
							},
						}
			),
			stack: series[i].stack,
		});
	}
	if (markLine) {
		seriesList.push({
			name: 'Markline',
			type: 'line',
			markLine: {
				...style.Tal.Line.series.markLine,
			},
		});
	}
	const visualMapOption = usePalette
		? {}
		: {
				visualMap: {
					type: 'continuous',
					inRange: {
						color: style.COLORS.generateGradient(belowOneTotalColors, aboveOneTotalColors, gradient),
					},
					min: minLift,
					max: maxLift,
					...style.VerticalBar.visualMap,
				},
			};
	const chartOption = {
		title: {
			text: title,
			...style.global.title,
		},
		grid: {
			...style.VerticalBar.grid,
		},
		textStyle: {
			...style.VerticalBar.textStyle,
		},
		label: {
			...style.VerticalBar.label,
		},
		backgroundColor: style.global.backgroundColor,
		toolbox: {
			...style.global.toolbox,
		},
		tooltip: {
			...style.VerticalBar.tooltip,
			formatter: ({ seriesIndex, dataIndex }) => {
				const likelihood = series[seriesIndex].values[dataIndex];
				const percentile = labels[dataIndex];
				const width = 100 / labels.length;
				return `
				<div style="padding: 8px;">
					Percentile bucket: ${percentile - width} - ${percentile}
					<br />
					Lift: ${likelihood.toFixed(1)}
				</div>
				`;
			},
		},
		legend: {
			data: series.map(({ name }) => name),
			show: series.length > 1,
			bottom: 20,
		},
		xAxis: {
			...style.VerticalBar.xAxis,
			data: labels,
		},
		yAxis: {
			...style.VerticalBar.yAxis,
			// NOTE: Typically if showRange is passed
			// the label should change
		},
		...visualMapOption,
		series: seriesList,
	};

	const mergedOptions = merge(chartOption, options);
	return (
		<Wrapper>
			<ReactEcharts
				ref={(element) => {
					const instance = element ? element.getEchartsInstance() : null;
					if (instance) {
						instance.off('mouseover');
						instance.on('mouseover', 'series', ({ dataIndex: index }) => {
							const label = labels[index];
							const values = series.map((_series) => _series.values[index]);
							onMouseover({ label, values });
						});
					}
				}}
				style={{
					width: '100%',
					height: `calc(100% - ${spreads.length ? '6.5' : showPalette ? '4.5' : '0'}rem)`,
				}}
				option={mergedOptions}
				theme={theme}
				notMerge
			/>
			{usePalette ? (
				showPalette ? (
					<Palette
						defaultTheme={defaultTheme}
						selected={theme}
						onSelect={(newTheme) => {
							setTheme(newTheme);
							onThemeChange(newTheme);
						}}
						style={{
							backgroundColor: mergedOptions.backgroundColor,
							width: '10rem',
							margin: '0 auto',
							paddingTop: '0',
						}}
					/>
				) : null
			) : (
				<Div d="flex" align="center" justify="flex-end">
					<Div
						tag="span"
						h="2.5rem"
						w="2.5rem"
						rounded="md"
						border="1px solid"
						borderColor="shadeE6"
						cursor="pointer"
						hoverBorderColor="shadeCC"
						data-tip="Blue-orange gradient"
						style={{
							...style.COLORS.BLUE_ORANGE_CSS,
						}}
						onClick={() => {
							setGradient(style.COLORS.GRADIENTS.BLUE_ORANGE);
							onGradientChange(style.COLORS.GRADIENTS.BLUE_ORANGE);
						}}
						m={{ l: '0.4rem' }}
					/>
					<Div
						tag="span"
						h="2.5rem"
						w="2.5rem"
						rounded="md"
						border="1px solid"
						borderColor="shadeE6"
						cursor="pointer"
						hoverBorderColor="shadeCC"
						data-tip="Blue-red gradient"
						style={{
							...style.COLORS.ELECTRIC_BLUE_RED_CSS,
						}}
						onClick={() => {
							setGradient(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
							onGradientChange(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
						}}
						m={{ l: '0.4rem' }}
					/>
					<Div
						tag="span"
						h="2.5rem"
						w="2.5rem"
						rounded="md"
						border="1px solid"
						borderColor="shadeE6"
						cursor="pointer"
						hoverBorderColor="shadeCC"
						data-tip="Full-spectrum gradient"
						style={{
							...style.COLORS.MULTICHROMATIC_CSS,
						}}
						onClick={() => {
							setGradient(style.COLORS.GRADIENTS.MULTICHROMA);
							onGradientChange(style.COLORS.GRADIENTS.MULTICHROMA);
						}}
						m={{ l: '0.4rem' }}
					/>
				</Div>
			)}
			{spreads.length ? (
				<Div d="flex" justify="space-around" align="center" h="3.5rem">
					{spreads.map((spread) => (
						<Div
							data-test="spreads"
							d="flex"
							textColor="white"
							p={{ x: '1rem', y: '0.75rem' }}
							m={{ y: '1.25rem' }}
							rounded="sm"
							style={{
								backgroundColor: 'rgba(50, 50, 50, 0.7)',
							}}
						>
							<Text textSize="subtitle">Top-bottom bucket spread: &nbsp;</Text>
							<Text textSize="subtitle">{spread.toFixed(1)}x</Text>
						</Div>
					))}
				</Div>
			) : null}
		</Wrapper>
	);
}

VerticalBar.propTypes = {
	title: PropTypes.string,
	labels: traitsPropType.isRequired,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			values: PropTypes.arrayOf(PropTypes.number),
		})
	).isRequired,
	style: stylePropType,
	options: PropTypes.shape({}),
	formatter: PropTypes.func,
	onMouseover: PropTypes.func,
	theme: PropTypes.string,
	gradient: PropTypes.string,
	markLine: PropTypes.bool,
	onGradientChange: PropTypes.func,
	onThemeChange: PropTypes.func,
	usePalette: PropTypes.bool,
	showPalette: PropTypes.bool,
	spreads: PropTypes.arrayOf(PropTypes.number),
};

VerticalBar.defaultProps = {
	title: '',
	style: Style1,
	options: {},
	formatter: (params) => params.value.toFixed(1),
	onMouseover: () => null,
	theme: 'pinpoint',
	gradient: Style1.COLORS.GRADIENTS.BLUE_ORANGE,
	markLine: true,
	onGradientChange: () => null,
	onThemeChange: () => null,
	usePalette: false,
	showPalette: true,
	spreads: [],
};

export default VerticalBar;
